import 'babel-polyfill';
import 'whatwg-fetch';

import { RAILS_ENV, RAVEN_URL } from './helpers/globals';
import { loadSentry } from './helpers/raven';

import '@opendoor/primer/stylesheets/primer.css';
// tslint:disable-next-line ordered-imports
import '@opendoor/bricks/stylesheets/bricks.extract.css';
import faviconPath from '@opendoor/shared-fe/build/faviconPath';

import { createBrowserHistory, createMemoryHistory, History } from 'history';
import { fromJS } from 'immutable';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { renderToString } from 'react-dom/server';
import { Helmet } from 'react-helmet';
import { Provider } from 'react-redux';
import { LOCATION_CHANGE, routerMiddleware, routerReducer } from 'react-router-redux';
import * as Redux from 'redux';
import { combineReducers } from 'redux-immutable';
import createSagaMiddleware from 'redux-saga';

import renderRoutes from './routes';
import trackPageView, { trackPathViews } from './routes/analytics';
import reducers from './store/reducers';
import sagas from './store/sagas';

// Inline segment for a request to analytics.js higher up in the render tree
import segmentInline from './constants/segmentInline';
import { SEGMENT_MARKET_JS_KEY } from './helpers/globals';

const { createStore, applyMiddleware, compose } = Redux;

const createApp = (store: Redux.Store<any>, history: History) => {
  return (
    <div>
      <Helmet>
        <title>Opendoor</title>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
        <meta name="viewport" content="width=device-width,initial-scale=1,user-scalable=no" />
        <meta name="fragment" content="!" />
        <meta httpEquiv="Accept-CH" content="DPR, Width, Viewport-Width" />
        <link rel="shortcut icon" href={faviconPath()} type="image/png" />
        <link rel="apple-touch-icon" sizes="144x144" href="/apple-touch-icon-144.png" />
        <script type="text/javascript">{segmentInline(SEGMENT_MARKET_JS_KEY)}</script>
      </Helmet>
      <Provider store={store}>{renderRoutes(history)}</Provider>
    </div>
  );
};

export const mainBrowser = (ravenUrl?: string) => {
  loadSentry(ravenUrl || RAVEN_URL);

  const sagaMiddleware = createSagaMiddleware();

  const history = createBrowserHistory();
  const routesMiddleware = routerMiddleware(history);

  trackPathViews({ pathname: location.pathname });
  history.listen((location, action) => {
    trackPageView(
      {
        pathname: location.pathname,
        search: location.search,
      },
      { 'homepage-2019-test': 'homepage-old-3' },
    );
    trackPathViews({ pathname: location.pathname });
  });

  let preloadedState = (window as any).__PRELOADED_STATE__;
  delete (window as any).__PRELOADED_STATE__;

  if (preloadedState) {
    preloadedState = fromJS(preloadedState);
  }

  const composeEnhancers =
    (RAILS_ENV !== 'production' && (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;

  const store = createStore(
    combineReducers({ ...reducers, router: routerReducer }),
    preloadedState,
    composeEnhancers(applyMiddleware(sagaMiddleware), applyMiddleware(routesMiddleware)),
  );
  sagaMiddleware.run(sagas);
  ReactDOM.render(createApp(store, history), document.getElementById('root'));
};

export const mainServer = (location: string, reduxState: any) => {
  const sagaMiddleware = createSagaMiddleware();

  const history = createMemoryHistory({ initialEntries: [location] });
  const routesMiddleware = routerMiddleware(history);

  const composeEnhancers = compose;

  if (reduxState) {
    reduxState = fromJS(reduxState);
  }

  const store = createStore(
    combineReducers({ ...reducers, router: routerReducer }),
    reduxState,
    composeEnhancers(applyMiddleware(sagaMiddleware), applyMiddleware(routesMiddleware)),
  );
  sagaMiddleware.run(sagas);

  store.dispatch({
    payload: { pathname: location, search: '', hash: '' },
    type: LOCATION_CHANGE,
  });

  const body = renderToString(createApp(store, history));
  const helmet = Helmet.renderStatic();
  return {
    body,
    head:
      helmet.title.toString() +
      helmet.meta.toString() +
      helmet.link.toString() +
      helmet.script.toString(),
  };
};
